import { Alert, Box, Stack } from "@mui/material";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useMyTrainings } from "../../hooks/useMyTrainings";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import {
  MapColorSpeedConfig,
  TrainingSetting,
} from "../../shared/types/trainingSetting.type";
import KeyParameters from "../../components/MyTrainings/KeyParameters";
import { TuserAccountType } from "../../shared/types/user.type";
import {
  TcomputedKeyParam,
  TkeyParamLayout,
} from "../../shared/types/computedKeyParam.type";
import { get, isEmpty, minBy, orderBy, uniqBy, upperCase } from "lodash";
import {
  getRandomElements,
  groupByCategory,
  mapTypeToTrainingKeys,
  reoderElements,
  replaceWithNewKey,
} from "../../shared/utils/myTrainings";
import { useExam } from "../../hooks/useExam";
import TrackViewSkeleton from "../../components/tracks/TrackViewSkeleton";
import GaitAverage from "../../components/MyTrainings/GaitAverage";
import MapSection from "../../components/exams/MapSection";
import MapMode from "../../components/MyTrainings/MapMode";
import ChartsSection from "../../components/exams/ChartsSection";
import { Scale } from "../trainings/ExamView";
import CheckpointsSection from "../../components/exams/CheckpointsSection";
import BaseAccordion from "../../components/MyTrainings/BaseAccordion";
import { useAuth } from "../../hooks/useAuth";
import { CheckPoint } from "../../shared/types/training.type";
import NotFound from "../../components/global/NotFound";
import { useMutation } from "react-query";
import axios, { AxiosResponse } from "axios";
import { TTransposedTraining } from "../../shared/types/transposedTraining.type";

const SAMPLE_EXAM_ID = 96836;

function MyTrainings() {
  const {
    trainingKeys,
    getPreselects,
    trainingKeysLoading,
    groupedTrainingKeys,
  } = useMyTrainings();
  const userSettings = useSelector<RootState>(
    (state) => state.user.user?.training_settings,
  ) as TrainingSetting;
  const mapColorType = useSelector<RootState>(
    (state) => state.user.user?.training_settings?.map_colors_type,
  ) as "gait" | "speed";
  const defaultScale = useSelector<RootState>(
    (state) => state.user.user?.training_settings?.default_graph_unit_type,
  ) as Scale;
  const mapSpeedConfig = useSelector<RootState>(
    (state) =>
      state.user.user?.training_settings?.map_colors_speed_configuration,
  ) as MapColorSpeedConfig;
  const [keyParamsLayout, setKeyParamsLayout] = useState<TcomputedKeyParam[]>(
    [],
  );
  const [deletedKeyParamsLayout, setDeletedKeyParamsLayout] = useState<
    TcomputedKeyParam[]
  >([]);
  const { exam, isExamLoading, examHasError } = useExam(
    false,
    null,
    SAMPLE_EXAM_ID,
  );
  const isFinishLineActive = exam?.training?.use_finish_line ?? false;
  const [distance, setDistance] = useState<string>("0 mi");
  const [speed, setSpeed] = useState<number[]>([]);
  const [bpm, setBpm] = useState<number[]>([]);
  const [time, setTime] = useState<number[]>([]);
  const [loCardio, setLoCardio] = useState<number[]>([]);
  const [rawDistance, setDistanceRaw] = useState<number[]>([]);
  const [lats, setLats] = useState<number[]>([]);
  const [lngs, setLangs] = useState<number[]>([]);
  const [strideFrequency, setStrideFrequency] = useState<number[]>([]);
  const [allure, setAllure] = useState<number[]>([]);
  const [strideLength, setStrideLength] = useState<number[]>([]);
  const [currentIndex, setCurrentindex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mapMode, setMapMode] = useState<"gait" | "speed" | undefined>(
    undefined,
  );
  const [scaleOption, setScaleOption] = useState<Scale>("DISTANCE");
  let id = useSelector<RootState>(
    (state) => state.user.user?.training_settings?.id,
  ) as number;
  const { updateUserTraingSetting, auth } = useAuth();
  const transposedMutation = useMutation(
    (): Promise<AxiosResponse<TTransposedTraining>> =>
      axios.get(exam?.training?.moments_transposed_file_url as string),
  );

  //If user type is polo we want to show 16 keys instead of 12
  const MAX_KEYS_TO_DISPLAY = auth?.type === "POLO" ? 16 : 12;

  useEffect(() => {
    if (exam?.training?.moments_transposed_file_url) {
      transposedMutation
        .mutateAsync()
        .then((res) => {
          let lts = sliceForRelevantMoment(res?.data.latitude ?? []);
          let lgs = sliceForRelevantMoment(res?.data.longitude ?? []);

          setLats(lts);
          setLangs(lgs);

          setSpeed(sliceForRelevantMoment(res?.data?.speed ?? []));
          setBpm(sliceForRelevantMoment(res?.data?.bpm ?? []));
          setDistanceRaw(sliceForRelevantMoment(res.data?.distance ?? []));
          setTime(sliceForRelevantMoment(res.data?.timestamp ?? []));
          setStrideFrequency(sliceForRelevantMoment(res.data?.pace ?? []));
          setStrideLength(sliceForRelevantMoment(res.data?.amplitude ?? []));
          setAllure(sliceForRelevantMoment(res.data?.allure ?? []));
          setLoCardio(
            sliceForRelevantMoment(
              res.data?.lo_cardio ?? res.data?.loCardio ?? [],
            ),
          );
        })
        .catch((err) => {
          console.log({ err });
        });
    }
  }, [
    exam?.training?.moments_transposed_file_url,
    exam?.training?.relevant_moments_index_start,
    exam?.training?.relevant_moments_index_end,
  ]);

  useEffect(() => {
    setScaleOption(upperCase(defaultScale) as Scale);
  }, [defaultScale]);

  useEffect(() => {
    setMapMode(mapColorType);
  }, [mapColorType]);
  useEffect(() => {
    if (exam) {
      setKeyParamsLayout(
        get(exam, "computed_key_parameters", []) as TcomputedKeyParam[],
      );

      let examGoodGps = exam?.training?.percentage_good_gps;

      if (
        typeof examGoodGps === "number" &&
        Number.isFinite(examGoodGps) &&
        examGoodGps < 20
      ) {
        setScaleOption("TIME");
      }
    }
  }, [exam]);

  const fetchPreselection = (preselect: TuserAccountType) => {
    getPreselects
      .mutateAsync({ type: preselect, nationality: auth.nationality })
      .then(({ data }) => {
        if (trainingKeys) {
          setKeyParamsLayout(
            mapTypeToTrainingKeys(trainingKeys, get(data, "hydra:member", [])),
          );
        }
      })
      .catch((err) => {
        console.log({ err });
      });
  };

  const onChangekey = (newId: string, oldParam: TcomputedKeyParam) => {
    if (!trainingKeys) return;
    setKeyParamsLayout(
      replaceWithNewKey(trainingKeys, newId, oldParam, keyParamsLayout),
    );
  };
  const deleteLine = () => {
    setKeyParamsLayout((prev) => {
      if (prev.length === 0) return prev;

      const removed = prev.slice(-4); // last 4
      setDeletedKeyParamsLayout((dPrev) => [...dPrev, ...removed]);

      return prev.slice(0, -4); // remove last 4
    });
  };

  const getLastElementOrder = () => {
    let element = keyParamsLayout[keyParamsLayout.length - 1];
    if (element) {
      return element.key_parameter_layout.order_number;
    } else {
      return 0;
    }
  };

  const sliceForRelevantMoment = (arr: number[]) => {
    let shouldSlice =
      exam?.training?.relevant_moments_index_start ||
      exam?.training?.relevant_moments_index_end;

    console.log({ shouldSlice });
    if (!shouldSlice) return arr;

    return arr.slice(
      exam?.training.relevant_moments_index_start ?? 0,
      exam?.training.relevant_moments_index_end ?? arr.length,
    );
  };

  const addLine = () => {
    // CASE 1: nothing to restore → fill with random
    if (
      isEmpty(deletedKeyParamsLayout) &&
      !isEmpty(trainingKeys?.items[0].computed_key_parameters)
    ) {
      const keyParamsLength = keyParamsLayout.length;
      const itemsToAdd = Math.max(0, MAX_KEYS_TO_DISPLAY - keyParamsLength);

      const elements = getRandomElements(
        trainingKeys?.items[0].computed_key_parameters ?? [],
        itemsToAdd,
      );

      const newElements = reoderElements(elements, getLastElementOrder());
      setKeyParamsLayout((prev) => [...prev, ...newElements]);

      return;
    }

    // CASE 2: restore from deleted stack
    setDeletedKeyParamsLayout((dPrev) => {
      const toRestore = dPrev.slice(-4); // take LAST 4 deleted
      setKeyParamsLayout((prev) => [...prev, ...toRestore]);

      return dPrev.slice(0, -4); // remove LAST 4
    });
  };

  const onModeChange = (value?: "gait" | "speed") => {
    setMapMode(value);
  };

  const filteredCheckPoints = useMemo(() => {
    let cps = exam?.training?.checkpoints;
    if (cps && cps.length > 0) {
      let checkpoints = uniqBy(
        cps.map((cp) => cp.checkpoint),
        "@id",
      );

      let checkPointsMin: CheckPoint[] = [];
      checkpoints.forEach((cp) => {
        let getDataBydId =
          cps?.filter((c) => c?.checkpoint?.["@id"] === cp?.["@id"]) ?? [];

        let data = minBy(getDataBydId, "index_moment");

        data && checkPointsMin.push(data);
      });
      return checkPointsMin;
    } else {
      return [];
    }
  }, [exam?.training?.checkpoints]);

  if (isExamLoading) {
    return <TrackViewSkeleton></TrackViewSkeleton>;
  }

  if (examHasError) {
    return (
      <Box
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"calc(100vh - 200px)"}
      >
        <NotFound></NotFound>
      </Box>
    );
  }

  return (
    <Stack direction={"column"} spacing={2}>
      <Alert severity="info">{t("trainingSettings:header")}</Alert>

      <KeyParameters
        loading={getPreselects.isLoading}
        trainingKeysLoading={trainingKeysLoading}
        computedKeys={keyParamsLayout}
        onPreselectChange={fetchPreselection}
        groupedKeys={groupedTrainingKeys}
        enabled={userSettings.display_section_key_parameters}
        onChangekey={onChangekey}
        trainingKeys={trainingKeys}
        deleteLine={deleteLine}
        addLine={addLine}
        maxParamKeysLength={MAX_KEYS_TO_DISPLAY}
      ></KeyParameters>
      <GaitAverage
        enabled={userSettings.display_section_averages}
        exam={exam}
      ></GaitAverage>
      <MapSection
        enabled={userSettings.display_section_map}
        enableCheckbox={true}
        allure={allure}
        pace={strideFrequency}
        setDistance={(value) => setDistance(value ?? "")}
        onIndexChange={(value) => setCurrentindex(value)}
        setIsLoading={(value) => setLoading(value)}
        transposedFile={exam?.training?.moments_transposed_file_url}
        chronology={exam?.averages_and_hr_zones?.gait_chronology}
        flIndex={exam?.training?.finish_line?.index_moment ?? null}
        isFinishLineActive={isFinishLineActive}
        finishLine={exam?.training?.finish_line}
        checkPoints={filteredCheckPoints}
        speed={speed}
        bpm={bpm}
        distance={rawDistance}
        time={time}
        relevant_index_start={exam?.training?.relevant_moments_index_start}
        relevant_index_end={exam?.training?.relevant_moments_index_end}
        currentIndex={currentIndex}
        lats={lats}
        lngs={lngs}
        strideLength={strideLength}
        showSave
      >
        <MapMode
          mapSpeedConfig={mapSpeedConfig}
          value={mapMode}
          onChange={onModeChange}
        ></MapMode>
      </MapSection>
      <ChartsSection
        showIntervalTraining
        loCardio={loCardio}
        editable
        strideFrequency={strideFrequency}
        strideLength={strideLength}
        time={time}
        rawDistance={rawDistance}
        speed={speed}
        bpm={bpm}
        distance={distance}
        scaleOption={scaleOption}
        onChangeScale={(val: Scale) => setScaleOption(val)}
        currentIndex={currentIndex}
        flIndex={exam?.training?.finish_line?.index_moment ?? null}
        loading={loading}
        heats={exam?.training?.heats ?? []}
        isFinishLineActive={isFinishLineActive}
        intervals={exam?.training?.sectionals ?? []}
        id={exam?.training?.id}
        percentageGoodCardio={exam?.training?.id ?? 100}
        onIndexChange={(index) => setCurrentindex(index)}
      ></ChartsSection>
      <BaseAccordion
        title="checkpointsCrossings:title"
        enableCheckBox
        defaultChecked={userSettings.display_section_checkpoints}
        onChange={(checked: boolean) =>
          updateUserTraingSetting(id, { display_section_checkpoints: checked })
        }
      >
        <CheckpointsSection
          onChangeScale={(val: Scale) => setScaleOption(val)}
          scaleOption={scaleOption}
          editable
          isFinishlineActive={isFinishLineActive}
          flIndex={exam?.training?.finish_line?.index_moment}
          checkPoints={exam?.training?.checkpoints ?? []}
          time={time}
          rawDistance={rawDistance}
          speed={speed}
          bpm={bpm}
        ></CheckpointsSection>
      </BaseAccordion>
    </Stack>
  );
}

export default MyTrainings;
